// TESSERAX — CLIP DE PARTAGE : enregistre en WebM/MP4 le MEILLEUR MOMENT de la run,
// REJOUÉ à travers le moteur vif (cf. replay.shareClip : startReplay + fenêtre {from,to}).
// Capture = canvas 2D COMPOSITE (frame WebGL + habillage score/marque, même registre que
// drawShareCard) → captureStream(30) → MediaRecorder. La copie du canvas WebGL se fait
// DANS la même frame que renderer.render (hook state._clipFrame consommé par loop.ts),
// donc pas de preserveDrawingBuffer. AUCUN impact core/déterminisme : pure relecture.
// Dégradation gracieuse : captureStream/MediaRecorder/codec absents → supported=false,
// le bouton n'apparaît pas, la carte PNG existante reste le partage par défaut.

import { state } from './state';
import { nfmt } from './util';
import { t } from '../i18n';
import type { SceneApi } from './scene';

export type ClipApi = ReturnType<typeof createClip>;

export function createClip(viz: SceneApi) {
  const src = viz.renderer && viz.renderer.domElement;
  // Codec : mp4/avc1 d'abord (Safari ne lit pas WebM), puis vp9/vp8. isTypeSupported peut
  // mentir par excès sur certains navigateurs → le try/catch de begin() reste le vrai filet.
  let mime = '';
  try {
    if (src && (src as any).captureStream && (window as any).MediaRecorder) {
      for (const m of ['video/mp4;codecs=avc1', 'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'])
        if (MediaRecorder.isTypeSupported(m)) { mime = m; break; }
    }
  } catch (e) {}
  const supported = !!mime;

  let cv: HTMLCanvasElement | null = null, g: CanvasRenderingContext2D | null = null,
      rec: MediaRecorder | null = null, chunks: Blob[] = [], onDone: ((b: Blob | null) => void) | null = null,
      endHold = 0, finalScore = 0;

  // Une frame de composition (appelée par la boucle JUSTE APRÈS renderer.render via state._clipFrame).
  function frame(): void {
    if (!g || !cv || !rec) return;
    try {
      g.drawImage(src, 0, 0, cv.width, cv.height);
      const W = cv.width, H = cv.height, band = Math.round(H * 0.075);
      g.save();
      g.fillStyle = 'rgba(4,8,18,0.55)'; g.fillRect(0, H - band, W, band);
      g.textBaseline = 'middle'; g.font = '700 ' + Math.round(band * 0.42) + 'px system-ui,sans-serif';
      g.fillStyle = '#7af0ff'; g.fillText('TESSERAX.xyz', Math.round(band * 0.4), H - band / 2);
      g.textAlign = 'right'; g.fillStyle = '#eaf4ff'; g.fillText(nfmt(state.score | 0), W - Math.round(band * 0.4), H - band / 2); g.textAlign = 'left';
      // Dernière seconde (endHold armé par end()) : le score FINAL en grand au centre — la chute du clip.
      if (endHold && performance.now() <= endHold) {
        g.textAlign = 'center'; g.fillStyle = 'rgba(4,8,18,0.45)'; g.fillRect(0, H * 0.36, W, H * 0.24);
        g.fillStyle = '#eaf4ff'; g.font = '800 ' + Math.round(H * 0.11) + 'px system-ui,sans-serif';
        g.fillText(nfmt(finalScore), W / 2, H * 0.48);
        g.fillStyle = '#7af0ff'; g.font = '700 ' + Math.round(H * 0.035) + 'px system-ui,sans-serif';
        g.fillText('TESSERAX', W / 2, H * 0.56); g.textAlign = 'left';
      }
      g.restore();
    } catch (e) {}
  }
  // Démarre l'enregistrement ; `done(blob)` est appelé quand le clip est assemblé (null si rien).
  function begin(done: (b: Blob | null) => void): boolean {
    if (!supported || rec || !src) return false;
    try {
      cv = document.createElement('canvas');
      const k = Math.min(1, 1280 / Math.max(src.width, src.height, 1));           // plafond 720p-ish, DPR replié
      cv.width = Math.max(2, Math.round(src.width * k)) & ~1; cv.height = Math.max(2, Math.round(src.height * k)) & ~1;   // dimensions PAIRES (codecs)
      g = cv.getContext('2d'); if (!g) throw 0;
      chunks = []; onDone = done; endHold = 0;
      rec = new MediaRecorder((cv as any).captureStream(30), { mimeType: mime, videoBitsPerSecond: 6_000_000 });
      rec.ondataavailable = e => { if (e.data && e.data.size) chunks.push(e.data); };
      rec.onstop = () => { const cb = onDone, bl = chunks.length ? new Blob(chunks, { type: mime.split(';')[0] }) : null; _reset(); if (cb) cb(bl); };
      rec.start(250);
      state._clipFrame = frame;
      return true;
    } catch (e) { _reset(); return false; }
  }
  // Clôture : fige le score final en grand ~0,8 s puis stoppe (le blob part via onstop → done).
  function end(): void {
    if (!rec) return; finalScore = state.score | 0; endHold = performance.now() + 800;
    const r = rec; setTimeout(() => { try { r.stop(); } catch (e) {} state._clipFrame = null; }, 840);
  }
  // Abandon (sortie de rejeu en cours de capture) : on jette tout, `done` n'est PAS appelé.
  function cancel(): void { const r = rec; onDone = null; _reset(); try { if (r && r.state !== 'inactive') r.stop(); } catch (e) {} }
  function _reset(): void { rec = null; cv = null; g = null; chunks = []; endHold = 0; state._clipFrame = null; }

  // Partage du blob : Web Share niveau 2 (fichiers) si possible, sinon téléchargement direct.
  // Appelé depuis un CLIC (geste utilisateur requis par navigator.share).
  function share(blob: Blob): void {
    const ext = mime.includes('mp4') ? 'mp4' : 'webm';
    const f = new File([blob], 'tesserax-clip.' + ext, { type: blob.type });
    const data: any = { files: [f], title: 'TESSERAX', text: t('clip.shareText', undefined, 'Mon meilleur moment TESSERAX ◆') };
    try { if ((navigator as any).canShare && (navigator as any).canShare(data) && navigator.share) { navigator.share(data).catch(() => {}); return; } } catch (e) {}
    try {
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = f.name;
      document.body.appendChild(a); a.click();
      setTimeout(() => { try { URL.revokeObjectURL(a.href); a.remove(); } catch (e) {} }, 4000);
    } catch (e) {}
  }
  return { supported, begin, end, cancel, share, recording: () => !!rec };
}
